import { PLATFORM_TYPES } from '@sitegen/common/constants/platforms';
import {
    setFetching,
    setRedemptionStatus,
    submitCode as submitCode_v1,
    redeemGift as redeemGift_v1
} from '../redemption.actions';
import {
    Benefit,
    BenefitsResponse,
    PLATFORM_CODE_TO_TYPE,
    PLATFORM_TYPE_TO_CODE,
    RedeemServiceError,
    SelectBenefitResponse
} from '@sitegen/common/types/redemption.types';
import { SAVE_BENEFITS, SAVE_REDEMPTION_PLATFORMS } from './redemption.constants';
import { Dispatch } from 'types/dispatch';
import redeemService from './redemption.service';
import { RECYCLE_REDEMPTION_PROCESS, REDEMPTION_STATUS } from '../redemption.constants';
import { parseRedeemServiceError } from './redemption.utils';
import { IRedeemCodeData } from 'types/redemption';

export const recycleRedemptionProcess = () => ({
    type: RECYCLE_REDEMPTION_PROCESS
});

export const savePlatforms = (payload: PLATFORM_TYPES[]) => ({
    type: SAVE_REDEMPTION_PLATFORMS,
    payload
});

export const saveBenefits = (payload: Benefit[]) => ({
    type: SAVE_BENEFITS,
    payload
});

/** Action wrapper for hybrid v1/v2 */
export const redeemGift = (redeemData: IRedeemCodeData) => (
    dispatch: Dispatch,
    getState: Function
) => {
    const state = getState();
    const isRedeemV1 = Boolean(state.redemption?.codeFirstPlatforms && !state.redemptionV2?.platforms);
    return isRedeemV1 ? dispatch(redeemGift_v1(redeemData)) : dispatch(submitCode(redeemData.platform!));
};

export const submitCode = (platform: PLATFORM_TYPES) => async (
    dispatch: Dispatch,
    getState: Function
) => {
    const state = getState();
    const {
        UbisoftConnectProfile: { user: { profileId } }
    } = state.login.loginSession;
    const { envName } = state.config;
    const { code } = state.redemption.redeemData;

    dispatch(setFetching(true));

    await redeemService.getBenefits({
        code, profileId, envName, platformType: PLATFORM_TYPE_TO_CODE[platform]
    }).then((benefits: BenefitsResponse) => {
        dispatch(saveBenefits(benefits.benefits));
        dispatch(setRedemptionStatus({ status: REDEMPTION_STATUS.SUCCESS }));
    }).catch((error) => {
        dispatch(setRedemptionStatus({ status: parseRedeemServiceError(error) }));
    }).finally(() => {
        dispatch(setFetching(false));
    });
};

export const runCodeCheck = () => async (
    dispatch: Dispatch,
    getState: Function
) => {
    const state = getState();
    const {
        UbisoftConnectTicket: ticket,
        UbisoftConnectProfile: { user: { profileId } },
        sessionId } = state.login.loginSession;
    const { applicationId, envName } = state.config;
    const { code } = state.redemption.redeemData;

    const handleError = async (error: RedeemServiceError) => {
        const redemptionError: REDEMPTION_STATUS = parseRedeemServiceError(error);
        switch (redemptionError) {
        case REDEMPTION_STATUS.SELECT_BENEFIT_REQUIRED: {
            // V2 fetch available platforms from selectable benefits
            await redeemService.getSelectableBenefits({
                code,
                profileId,
                envName
            }).then(( selectableBenefits: SelectBenefitResponse ) => {
                dispatch(saveBenefits(selectableBenefits.selectableBenefits));
                const platforms = [ ...new Set(selectableBenefits.selectableBenefits.map( benefit => benefit.platformType ))]
                    .map( redeemPlatform => PLATFORM_CODE_TO_TYPE[redeemPlatform]);
                dispatch(savePlatforms(platforms));
            },
            err => {
                dispatch(setRedemptionStatus({
                    status: parseRedeemServiceError(err)
                }));
            });
            break;
        }
        case REDEMPTION_STATUS.INVALID_CODE: {
            // V1 fetch available platforms
            dispatch(submitCode_v1(code));
            break;
        }
        default: {
            dispatch(setRedemptionStatus({ status: redemptionError }));
        }
        }
    };

    redeemService.configure({
        applicationId, sessionId, ticket, profileId
    });

    dispatch(setFetching(true));

    await redeemService.getBenefits({ code, profileId, envName })
        .then(
            (benefits: BenefitsResponse) => {
                dispatch(saveBenefits(benefits.benefits));
                dispatch(setRedemptionStatus({ status: REDEMPTION_STATUS.SUCCESS }));
            },
            (error: RedeemServiceError) => handleError(error))
        .catch( error => handleError(error))
        .finally(() => {
            dispatch(setFetching(false));
        });
};

export type RedemptionActions = ReturnType<typeof setFetching>
| ReturnType<typeof savePlatforms>
| ReturnType<typeof setRedemptionStatus>
| ReturnType<typeof saveBenefits>
| ReturnType<typeof recycleRedemptionProcess>;
